"use client";

import { useState } from "react";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@workspace/ui/components/tabs";
import { Button } from "@workspace/ui/components/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import {
  PanelRightClose,
  History as HistoryIcon,
  Sparkles,
  FileText,
  Palette,
  GitCommit,
  X,
} from "lucide-react";
import { History } from "./history";
import { AiPanel } from "./ai-panel";
import { Commits } from "./commits";
import { PropertiesPanel } from "./properties-panel";
import { ThemePanel } from "@/components/theme/theme-panel";
import type { useDocTheme } from "@/lib/theme/use-doc-theme";

interface SidebarPanelProps {
  documentId: string;
  getEditorContent?: () => string;
  hasFrontmatter?: boolean;
  themeState: ReturnType<typeof useDocTheme>;
}

const TABS = [
  { value: "properties", label: "Properties", Icon: FileText },
  { value: "versions", label: "History", Icon: HistoryIcon },
  { value: "commits", label: "Commits", Icon: GitCommit },
  { value: "theme", label: "Theme", Icon: Palette },
  { value: "ai", label: "AI", Icon: Sparkles },
] as const;

/**
 * The tabbed panel itself, with no chrome of its own, so the docked sidebar
 * and the mobile drawer share one implementation. `closeMode` only changes the
 * close button's icon and label: collapse to a rail, or dismiss a drawer.
 */
export function SidebarPanel({
  documentId,
  getEditorContent,
  hasFrontmatter,
  themeState,
  onClose,
  closeMode,
}: SidebarPanelProps & { onClose: () => void; closeMode: "collapse" | "dismiss" }) {
  // `hasFrontmatter` arrives from a SWR fetch, so it is `undefined` on the
  // first render and only becomes true a moment later. Radix reads
  // `defaultValue` once at mount and never again, so passing it directly locked
  // every document onto "versions" — the properties-by-default behaviour was
  // written and then never observed, because the data always lost the race.
  //
  // Holding the user's explicit choice (null until they pick a tab) lets the
  // resolved default apply the instant it arrives, while a later fetch can
  // never yank the panel out from under someone who has since clicked.
  const [tabChoice, setTabChoice] = useState<string | null>(null);
  const activeTab = tabChoice ?? (hasFrontmatter ? "properties" : "versions");

  const closeLabel = closeMode === "collapse" ? "Collapse panel" : "Close panel";
  const CloseIcon = closeMode === "collapse" ? PanelRightClose : X;

  return (
    <Tabs
      value={activeTab}
      onValueChange={setTabChoice}
      className="flex-1 flex flex-col min-h-0"
    >
      {/* Five tabs don't fit in the w-96 panel with text labels, so the
          labels move into tooltips and the triggers become icon-only. */}
      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-1 mx-3 shrink-0">
          <TabsList className="bg-unset">
            {TABS.map(({ value, label, Icon }) => (
              <Tooltip key={value}>
                <TooltipTrigger asChild>
                  <TabsTrigger
                    value={value}
                    aria-label={label}
                    className="px-2.5"
                  >
                    <Icon className="h-4 w-4" />
                  </TabsTrigger>
                </TooltipTrigger>
                <TooltipContent>{label}</TooltipContent>
              </Tooltip>
            ))}
          </TabsList>

          {/* Deliberately outside <TabsList>: inside, it would sit on the
              `bg-muted` fill and read as a sixth tab. `h-9` matches the
              list's height so the two align on one row. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-9 w-9 ml-auto shrink-0"
                aria-label={closeLabel}
              >
                <CloseIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{closeLabel}</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
      <TabsContent
        value="properties"
        className="flex-1 min-h-0 mt-0 data-[state=active]:flex flex-col"
      >
        <PropertiesPanel documentId={documentId} />
      </TabsContent>
      <TabsContent
        value="versions"
        className="flex-1 min-h-0 mt-0 data-[state=active]:flex flex-col"
      >
        <History documentId={documentId} />
      </TabsContent>
      <TabsContent
        value="commits"
        className="flex-1 min-h-0 mt-0 data-[state=active]:flex flex-col"
      >
        <Commits documentId={documentId} />
      </TabsContent>
      <TabsContent
        value="theme"
        className="flex-1 min-h-0 mt-0 data-[state=active]:flex flex-col"
      >
        <ThemePanel state={themeState} />
      </TabsContent>
      <TabsContent
        value="ai"
        className="flex-1 min-h-0 mt-0 data-[state=active]:flex flex-col"
      >
        <AiPanel
          documentId={documentId}
          getEditorContent={getEditorContent}
        />
      </TabsContent>
    </Tabs>
  );
}
